import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

// Convert base64 URL-safe string to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushNotificationManager() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    try {
      // Detect iOS
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      setIsIOS(iOS);
      
      // Check if running as installed PWA (standalone mode)
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsStandalone(standalone);

      // Check if push notifications are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push not supported: missing serviceWorker or PushManager');
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      // Check if Notification API is available
      if (!('Notification' in window)) {
        console.log('Notification API not available');
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      // iOS requires PWA to be installed for push notifications
      if (iOS && !standalone) {
        console.log('iOS detected but not in standalone mode');
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      setIsSupported(true);
      setPermission(Notification.permission);

      // Check current subscription status with timeout
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Service worker timeout')), 5000)
        );
        
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          timeoutPromise
        ]);
        
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (swError) {
        console.error('Service worker error:', swError);
        // Still allow attempting to subscribe even if check failed
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error('Error in checkSupport:', err);
      setError(err.message);
      setIsSupported(false);
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeToPush = async () => {
    setIsLoading(true);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Please allow notifications to stay updated on matches');
        setIsLoading(false);
        return;
      }

      // Register service worker if not already registered
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const keyResponse = await axios.get(`${API_URL}/api/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.public_key;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      // Use the subscription's native toJSON() which returns properly formatted keys
      const subJson = subscription.toJSON();
      const subscriptionData = {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      };
      
      console.log('[PushNotifications] Subscription data:', {
        endpoint: subscriptionData.endpoint?.substring(0, 60) + '...',
        p256dh_length: subscriptionData.keys?.p256dh?.length,
        auth_length: subscriptionData.keys?.auth?.length
      });

      await axios.post(`${API_URL}/api/push/subscribe`, subscriptionData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });

      setIsSubscribed(true);
      toast.success('Push notifications enabled! You\'ll receive match updates.');
      
      // Send a test notification
      try {
        await axios.post(`${API_URL}/api/push/test`, {}, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        });
      } catch (e) {
        // Test notification is optional
      }

    } catch (error) {
      console.error('Error subscribing to push:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to enable notifications: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove subscription from backend
        await axios.delete(`${API_URL}/api/push/unsubscribe`, {
          data: {
            endpoint: subscription.endpoint,
            keys: {}
          },
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        });
      }

      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if not supported
  if (!isSupported) {
    // Show iOS install hint if on iOS but not in standalone mode
    if (isIOS && !isStandalone) {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-400"
          title="Add to Home Screen first to enable notifications"
          onClick={() => toast.info('To enable notifications on iOS, tap the Share button and select "Add to Home Screen", then open from there.')}
          data-testid="notifications-ios-hint"
        >
          <Bell className="w-5 h-5" />
        </Button>
      );
    }
    return null;
  }

  // Show blocked state
  if (permission === 'denied') {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled
        className="text-slate-400"
        title="Notifications blocked. Please enable in browser settings."
        data-testid="notifications-blocked"
      >
        <BellOff className="w-5 h-5" />
      </Button>
    );
  }

  const sendTestNotification = async () => {
    try {
      await axios.post(`${API_URL}/api/push/test`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      toast.success('Test notification sent! Check your notifications.');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
        disabled={isLoading}
        className={isSubscribed ? 'text-green-600' : 'text-slate-600 hover:text-[#E63946]'}
        title={isSubscribed ? 'Notifications enabled (tap to disable)' : 'Enable notifications'}
        data-testid="notifications-toggle"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="w-5 h-5" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
      </Button>
      {isSubscribed && (
        <Button
          variant="ghost"
          size="sm"
          onClick={sendTestNotification}
          className="text-slate-500 hover:text-[#E63946] text-xs px-2"
          title="Send test notification"
          data-testid="test-notification-btn"
        >
          Test
        </Button>
      )}
    </div>
  );
}

export function NotificationPrompt({ onDismiss }) {
  const [isSupported, setIsSupported] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    checkIfShouldShow();
  }, []);

  const checkIfShouldShow = async () => {
    // Check support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }
    setIsSupported(true);

    // Don't show if already granted or denied
    if (Notification.permission !== 'default') {
      return;
    }

    // Don't show if already subscribed
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        return;
      }
    } catch (e) {
      // Continue anyway
    }

    // Check if user dismissed recently (localStorage)
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    setShowPrompt(true);
  };

  const handleDismiss = () => {
    localStorage.setItem('notification_prompt_dismissed', new Date().toISOString());
    setShowPrompt(false);
    if (onDismiss) onDismiss();
  };

  const handleEnable = async () => {
    setShowPrompt(false);
    
    try {
      const permissionResult = await Notification.requestPermission();
      
      if (permissionResult === 'granted') {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // Get VAPID key
        const keyResponse = await axios.get(`${API_URL}/api/push/vapid-public-key`);
        const vapidPublicKey = keyResponse.data.public_key;

        // Subscribe
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        // Send to backend - use native toJSON() for proper key format
        const subJson = subscription.toJSON();
        const subscriptionData = {
          endpoint: subJson.endpoint,
          keys: subJson.keys
        };

        await axios.post(`${API_URL}/api/push/subscribe`, subscriptionData, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        });

        toast.success('Notifications enabled!');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    }
  };

  if (!isSupported || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-[#E63946]/10 rounded-lg">
          <Bell className="w-6 h-6 text-[#E63946]" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#0F172A]">Stay Updated!</h3>
          <p className="text-sm text-slate-600 mt-1">
            Get notified about new matches, RSVP reminders, and payment updates.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleEnable}
              size="sm"
              className="bg-[#E63946] hover:bg-[#DC2626] text-white"
              data-testid="enable-notifications-btn"
            >
              Enable
            </Button>
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="text-slate-600"
              data-testid="dismiss-notifications-btn"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
